import apolloSetup from '@/assets/modules/apollo/apolloSetup'

export default {
  buildGqlObject (baseFields, type, gqlType, media = [], forcedNames = {}, fetchFields = null) {
    // Prepare data object based on chosen type...
    let dataObject = buildDataObject(baseFields, type) || {}

    // Get mutation name from that type, in case they differ...
    let mutationName = getMutationName(type)

    // Prepare head and params field for the gql object...
    let {head, params} = apolloSetup.makeApolloHeadString(dataObject, forcedNames)
    // Specify response fields...
    let responseFields = fetchFields || getResponseFields(type, baseFields)

    // Build a graphql string from this data...
    let gqlBase = {
      type: gqlType,
      name: mutationName,
      head,
      params,
      fields: responseFields
    }

    let mutationString = apolloSetup.setupGqlString(gqlBase)

    return {
      mutationString,
      dataObject,
      name: mutationName
    }
  }
}

function buildDataObject (baseFields, type, media) {
  switch (type) {
    case 'promoteProduct':
      baseFields.type = 'productPromotion'
      return buildCampaign(baseFields, media)

    case 'saleProduct':
      baseFields.type = 'sale'
      return buildCampaign(baseFields, media)

    case 'dailyDeal':
      baseFields.type = 'dailyDeal'
      return buildCampaign(baseFields, media)

    case 'storeUser':
      return buildStoreUser(baseFields)

      case 'migrateSqwiz':
        return buildMigrateSqwiz(baseFields)

    case 'assignRoleMember':
      return buildAssignRoleMember(baseFields)

    case 'removeRoleMember':
      return buildRemoveRoleMember(baseFields)

    case 'postSubmission':
      return buildAdvertisableProduct(baseFields, media)

    case 'newsSubmission':
      return buildAdvertisablePost(baseFields, media)

    case 'storeAdvertisable':
      return buildStoreAdvertisable(baseFields, media)

    case 'storeMpe':
      return buildStoreMpe(baseFields, media)

    case 'updateCampaign':
      return buildUpdateCampaign(baseFields, media)

    case 'AdvertisableImportedProduct':
      return buildAdvertisableImportedProduct(baseFields, media)

    case 'createSqwizPage':
      return buildCreateSqwizPage(baseFields, media)

    case 'storeUserCredentials':
      return buildStoreUserCredentials(baseFields, media)

    case 'storeMediaPlanEvent':
      return buildStoreMpe(baseFields)

    case 'storeMedia':
      return buildStoreMedia(baseFields)

    case 'passwordReset':
      return buildPasswordReset(baseFields)

    case 'requestPasswordResetToken':
      return buildRequestResetToken(baseFields)

    case 'regeneratePasswordResetToken':
      return buildRegenPasswordResetToken(baseFields)

    case 'signUp':
      return buildSignUp(baseFields)

    case 'signIn':
      return buildSignIn(baseFields)

    case 'resendEmail':
      return buildResendEmail(baseFields)

    case 'validateEmailValidationToken':
      return buildValidateEmailToken(baseFields)

    case 'deleteUserCredentials':
      return buildDeleteUserCredentials(baseFields)

    case 'removePublishedMediaPlanEvent':
      return buildRemovePublishedMediaPlanEvent(baseFields)

    case 'deleteMediaPlanEvent':
      return buildDeleteMediaPlanEvent(baseFields)

    case 'deleteMedia':
      return buildDeleteMedia(baseFields)

    case 'deleteCampaign':
      return buildDeleteCampaign(baseFields)

    case 'deleteAdvertisable':
      return buildDeleteAdvertisable(baseFields)

    case 'publishMediaPlanEventPayload':
      return buildPublishMpePayload(baseFields)

    case 'generateCampaignEvents':
      return buildGenerateCampaignEvents(baseFields)

    case 'storeContent':
      return buildStoreContent(baseFields)

    case 'impersonate':
      return buildImpersonate(baseFields)

    case 'recordEvent':
      return buildRecordEvent(baseFields)

    case 'importProducts':
      return buildImportProducts(baseFields)
  }
}

function getResponseFields (type, baseFields = null) {
  switch (type) {
    case 'promoteProduct':
      return `
        id,
        label,
        status,
        events {
          id
        },
        startsAt,
        expiresAt
      `

    case 'createSqwizPage':
      return `
        id,
        type,
        label,
        identity,
        meta {
          ... on UserCredentialsSqwizPage {
            viewUrl,
            editUrl
          }
        }
      `

    case 'storeUserCredentials':
      return `
        id,
        type,
        meta {
          ... on UserCredentialsWordpressWebsite {
            url
          }
        }
      `

    case 'storeMedia':
      return `
        id,
        url
      `

    case 'storeMediaPlanEvent':
      return `
        id
      `

    case 'saleProduct':
      return `
        id,
        label,
        status,
        events {
          id
        },
        startsAt,
        expiresAt
      `

    case 'dailyDeal':
      return `
        id,
        label,
        status,
        events {
          id
        },
        startsAt,
        expiresAt
      `

    case 'storeUser':
      return `
        id
      `

    case 'migrateSqwiz':
        return `createFails, deleteFails`

    case 'assignRoleMember':
      return ``

    case 'removeRoleMember':
      return ``

    case 'postSubmission':
      return `
        id,
        url,
        label,
        description,
        price,
        discount,
        discountValidity,
        tags
      `

    case 'newsSubmission':
      return `
        id,
        url,
        label,
        content,
        tags
      `
    case 'storeAdvertisable':
      // Extra fields based on advertisable type
      let extraFields = ''

      switch (baseFields.type) {
        case 'importedProduct':
          extraFields = `meta {
            ... on AdvertisableImportedProduct {
              description,
              vendor,
              price,
              discount
              tags
            }
          }`

          break
      }

      return `
        id,
        media {
          set {
            url
          }
        }
      `

    case 'storeMpe':
      return `
        id,
        publishAt
      `

    case 'updateCampaign':
      return `
        id
      `

    case 'AdvertisableImportedProduct':
      return `
        id,
        url,
        label,
        createdAt,
        updatedAt,
        deletedAt,
        description,
        vendor,
        price,
        discount,
        tags
      `

    case 'passwordReset':
      return `
        token,
        user {
          id,
          email,
          name,
          lockedAt
        }
      `

    case 'requestPasswordResetToken':
      return ``

    case 'regeneratePasswordResetToken':
      return ``

    case 'signUp':
      return `
        token,
        user {
          id,
          email,
          name,
          settings {
            showOnboarding
          }
        }
      `

    case 'signIn':
      return `
        token,
        user {
          id,
          email,
          name,
          lockedAt,
          settings {
            showOnboarding
          }
        }
      `

    case 'resendEmail':
      return ``

    case 'validateEmailValidationToken':
      return `
        token,
        user {
          id,
          email,
          name,
          settings {
            showOnboarding
          }
        }
      `

    case 'deleteUserCredentials':
      return ``

    case 'removePublishedMediaPlanEvent':
      return ``

    case 'deleteMediaPlanEvent':
      return ``

    case 'deleteMedia':
      return ``

    case 'deleteCampaign':
      return ``

    case 'deleteAdvertisable':
      return ``

    case 'publishMediaPlanEventPayload':
      return ``

    case 'generateCampaignEvents':
      return `
        set {
          id
        }
      `

    case 'storeContent':
      return `id`

    case 'impersonate':
      return `
        id: token,
        token,
        user {
          id,
          name,
          email,
          permissions {
            addUsers,
            addCampaigns,
            addAdvertisable,
            addMediaPlanEvent,
            addMedia,
            addContent
          },
          roles {
            id,
            label
          },
          settings {
            language
          },
          contact {
            id: vatId,
            companyName
          }
        }
      `

    case 'recordEvent':
      return ``

    case 'importProducts':
      return ``
  }
}

function getMutationName (type) {
  switch (type) {
    case 'promoteProduct':
      return 'storeCampaign'

    case 'saleProduct':
      return 'storeCampaign'

    case 'dailyDeal':
      return 'storeCampaign'

    case 'storeUser':
      return 'storeUser'

    case 'migrateSqwiz':
      return 'migrateSqwiz'

    case 'assignRoleMember':
      return 'assignRoleMember'

    case 'removeRoleMember':
      return 'removeRoleMember'

    case 'createSqwizPage':
      return 'createSqwizPage'

    case 'postSubmission':
      return 'storeAdvertisableProduct'

    case 'newsSubmission':
      return 'storeAdvertisablePost'

    case 'storeMpe':
      return 'storeMediaPlanEvent'

    case 'storeMedia':
      return 'storeMedia'

    case 'storeAdvertisable':
      return 'storeAdvertisable'

    case 'updateCampaign':
      return 'storeCampaign'

    case 'AdvertisableImportedProduct':
      return 'storeAdvertisableImportedProduct'

    case 'storeUserCredentials':
      return 'storeUserCredentials'

    case 'storeMediaPlanEvent':
      return 'storeMediaPlanEvent'

    case 'passwordReset':
      return 'passwordReset'

    case 'requestPasswordResetToken':
      return 'requestPasswordResetToken'

    case 'regeneratePasswordResetToken':
      return `regeneratePasswordResetToken`

    case 'signUp':
      return 'signUp'

    case 'signIn':
      return 'signIn'

    case 'resendEmail':
      return 'resendEmailValidationToken'

    case 'validateEmailValidationToken':
      return 'validateEmailValidationToken'

    case 'deleteUserCredentials':
      return 'deleteUserCredentials'

    case 'removePublishedMediaPlanEvent':
      return `removePublishedMediaPlanEvent`

    case 'deleteMediaPlanEvent':
      return 'deleteMediaPlanEvent'

    case 'deleteMedia':
      return 'deleteMedia'

    case 'deleteCampaign':
      return 'deleteCampaign'

    case 'deleteAdvertisable':
      return 'deleteAdvertisable'

    case 'publishMediaPlanEventPayload':
      return 'publishMediaPlanEventPayload'

    case 'generateCampaignEvents':
      return 'generateCampaignEvents'

    case 'storeContent':
      return 'storeContent'

    case 'impersonate':
      return 'impersonate'

    case 'recordEvent':
      return `recordEvent`

    case 'importProducts':
      return `importProducts`
  }
}

// ---
function buildStoreUser (baseFields) {
  let rtr = {
    id: baseFields.id,
    email: baseFields.email,
    password: baseFields.password,
    oldPassword: baseFields.oldPassword,
    settings: baseFields.settings,
    contact: baseFields.contact
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildMigrateSqwiz (baseFields) {
  let rtr = {
    credentials: baseFields.credentials,
    userId: baseFields.userId
  }

  return clearBlank(rtr)
}

function buildAssignRoleMember (baseFields) {
  let rtr = {
    user: baseFields.user,
    role: baseFields.role,
    activates: baseFields.activates,
    expires: baseFields.expires,
    enabled: baseFields.enabled
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildRemoveRoleMember (baseFields) {
  let rtr = {
    user: baseFields.user,
    role: baseFields.role
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildCampaign (baseFields, media) {
  let rtr = {
    label: baseFields.label,
    startsAt: baseFields.startsAt,
    expiresAt: baseFields.expiresAt,
    type: baseFields.type
  }

  if (baseFields.productPromotion) {
    rtr.productPromotion = baseFields.productPromotion
  }
  if (baseFields.dailyDeal) {
    rtr.dailyDeal = baseFields.dailyDeal
  }
  if (baseFields.sale) {
    rtr.sale = baseFields.sale
  }

  return rtr
}

function buildAdvertisableProduct (baseFields, media) {
  let rtr = {
    url: baseFields.url,
    label: baseFields.label,
    description: baseFields.description,
    price: baseFields.price,
    discount: baseFields.discount,
    // discountValidity: baseFields.discountValidity,
    tags: baseFields.tags
  }

  return rtr
}

function buildAdvertisablePost (baseFields, media) {
  let rtr = {
    url: baseFields.url,
    label: baseFields.label,
    content: baseFields.description,
    tags: baseFields.tags
  }

  return rtr
}

function buildStoreAdvertisable (baseFields, media) {
  let rtr = {
    url: baseFields.url,
    label: baseFields.label,
    type: baseFields.type
  }

  if (baseFields.importedProduct) {
    rtr.product = baseFields.importedProduct
    rtr.type = 'product'
  }
  if (baseFields.product) {
    rtr.product = baseFields.product
  }
  if (baseFields.post) {
    rtr.post = baseFields.post
  }

  if (baseFields.id) {
    rtr.id = baseFields.id
  }

  return rtr
}

function buildStoreMpe (baseFields, media) {
  let rtr = {
    id: baseFields.id,
    advertisable: baseFields.advertisable,
    campaign: baseFields.campaign,
    type: baseFields.type,
    publishAt: baseFields.publishAt || baseFields.startsAt,
    removeAt: baseFields.removeAt || baseFields.expiresAt,
    forceAutoTransformation: baseFields.forceAutoTransformation,

    sqwizArticle: baseFields.sqwizArticle,
    sqwizProduct: baseFields.sqwizProduct,
    facebookPost: baseFields.facebookPost,
    shopamineProduct: baseFields.shopamineProduct,
    informacijaProduct: baseFields.informacijaProduct,
    informacijaPost: baseFields.informacijaPost,
    shopaminePost: baseFields.shopaminePost,
    twitterTweet: baseFields.twitterTweet,
    pinterestPin: baseFields.pinterestPin,
    wordpressPost: baseFields.wordpressPost,
    woocommerceProduct: baseFields.woocommerceProduct,

    credentials: baseFields.credentials
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildUpdateCampaign (baseFields, media) {
  // First prepare all possible fields, than remove unneeded ones...
  let rtr = {
    id: baseFields.id,
    label: baseFields.label,
    expiresAt: baseFields.expiresAt,
    startsAt: baseFields.startsAt || baseFields.expiresAt,
    type: baseFields.type,
    tags: baseFields.tags,
    status: baseFields.status,
    advertisable: baseFields.advertisable
  }

  if (baseFields.status) {
    switch (baseFields.status) {
      case 'activate':
        rtr.status = 'active'
        break

      case 'draft':
        rtr.status = 'draft'
        break

      case 'archived':
        rtr.status = 'archived'
        break
    }
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildAdvertisableImportedProduct (baseFields, media) {
  let rtr = {
    id: baseFields.id,
    url: baseFields.url,
    label: baseFields.label,
    description: baseFields.description,
    vendor: baseFields.vendor,
    price: baseFields.price,
    discount: baseFields.discount,
    tags: baseFields.tags
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildCreateSqwizPage (baseFields, media) {
  let rtr = {
    address: baseFields.address,
    category: parseInt(baseFields.category) || undefined,
    city: baseFields.city,
    culture: baseFields.culture,
    phone: baseFields.phone,
    postalcode: baseFields.postalcode,
    title: baseFields.title,
    portal: baseFields.portal,

    facebook: baseFields.facebook
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildStoreUserCredentials (baseFields, media) {
  let rtr = {
    type: baseFields.type,
    owner: baseFields.owner,
    credentials: baseFields.credentials,
    label: baseFields.label,
    visible: baseFields.visible
  }
  if (baseFields.id) {
    rtr.id = baseFields.id
  }
  if (baseFields.revalidate) {
    rtr.revalidate = baseFields.revalidate
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildStoreMedia (baseFields) {
  let rtr = {
    refType: baseFields.refType,
    ref: baseFields.ref,
    remoteUrl: baseFields.remoteUrl,
    async: baseFields.async
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildPasswordReset (baseFields) {
  let rtr = {
    token: baseFields.token,
    password: baseFields.password
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildRequestResetToken (baseFields) {
  let rtr = {
    email: baseFields.email
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildRegenPasswordResetToken (baseFields) {
  let rtr = {
    token: baseFields.token
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildSignUp (baseFields) {
  let rtr = {
    email: baseFields.email,
    password: baseFields.password,
    settings: baseFields.settings
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildSignIn (baseFields) {
  let rtr = {
    email: baseFields.email,
    password: baseFields.password
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildResendEmail (baseFields) {
  let rtr = {
    email: baseFields.email
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildValidateEmailToken (baseFields) {
  let rtr = {
    token: baseFields.token
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildDeleteUserCredentials (baseFields) {
  let rtr = {
    id: baseFields.id
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildRemovePublishedMediaPlanEvent (baseFields) {
  let rtr = {
    id: baseFields.id
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildDeleteMediaPlanEvent (baseFields) {
  let rtr = {
    id: baseFields.id
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildDeleteMedia (baseFields) {
  let rtr = {
    id: baseFields.id
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildDeleteCampaign (baseFields) {
  let rtr = {
    id: baseFields.id
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildDeleteAdvertisable (baseFields) {
  let rtr = {
    id: baseFields.id
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildPublishMpePayload (baseFields) {
  let rtr = {
    id: baseFields.id
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildGenerateCampaignEvents (baseFields) {
  let rtr = {
    campaign: baseFields.campaign,
    advertisables: baseFields.advertisables,
    credentials: baseFields.credentials,
    store: baseFields.store,
    truncate: baseFields.truncate
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildStoreContent (baseFields) {
  let rtr = {
    id: baseFields.id,
    key: baseFields.key,
    type: baseFields.type,
    language: baseFields.language,
    page: baseFields.page,
    snippet: baseFields.snippet,
    email: baseFields.email
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildImpersonate (baseFields) {
  let rtr = {
    id: baseFields.id
  }

  rtr = clearBlank(rtr)

  return rtr
}

function buildRecordEvent (baseFields) {
  let rtr = {
    type: baseFields.type
  }

  return clearBlank(rtr)
}

function buildImportProducts (baseFields) {
  let rtr = {
    credentialsId: baseFields.credentialsId
  }

  return clearBlank(rtr)
}

function clearBlank (fields) {
  for (let field of Object.keys(fields)) {
    if (fields[field] === undefined) {
      delete fields[field]
    }
  }
  return fields
}



// WEBPACK FOOTER //
// ./src/assets/modules/apollo/fieldBuilder.js